import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Empregado {
  id: string;
  workspaceId: string;
  acordoId: string | null;
  cpf: string;
  nome: string;
  cargo: string | null;
  funcao: string | null;
  cargaHorariaSemanal: number | null;
  salarioBase: string | null;
  dataAdmissao: string | null;
  dataDesligamento: string | null;
  naturezaContratacao: string | null;
  formacao: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface EmpregadosListResult {
  items: Empregado[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmpregadoFilters {
  search?: string;
  ativos?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateEmpregadoInput {
  cpf: string;
  nome: string;
  cargo?: string;
  funcao?: string;
  cargaHorariaSemanal?: number;
  salarioBase?: string;
  dataAdmissao?: string;
  dataDesligamento?: string;
  naturezaContratacao?: string;
  formacao?: string;
  acordoId?: string;
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/empregados`;
}

function buildQuery(filters: EmpregadoFilters): string {
  const q = new URLSearchParams();
  if (filters.search) q.set('search', filters.search);
  if (filters.ativos != null) q.set('ativos', String(filters.ativos));
  if (filters.limit != null) q.set('limit', String(filters.limit));
  if (filters.offset != null) q.set('offset', String(filters.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useEmpregados(workspaceId: string | null | undefined, filters: EmpregadoFilters) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['empregados', workspaceId, filters],
    queryFn: () => api.get<EmpregadosListResult>(`${basePath(workspaceId!)}/${buildQuery(filters)}`),
  });
}

export function useEmpregadosAggregate(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['empregados', workspaceId, 'aggregate'],
    queryFn: () => api.get<{ ativos: number }>(`${basePath(workspaceId!)}/aggregate`),
  });
}

export function useCreateEmpregado(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEmpregadoInput) =>
      api.post<Empregado>(`${basePath(workspaceId!)}/`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empregados', workspaceId] });
    },
  });
}

export function useDeleteEmpregado(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empregados', workspaceId] });
    },
  });
}
